import type { z as zod } from 'zod';

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Grid from '@mui/material/Grid2';
import { Box, Button } from '@mui/material';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';
import { ChannelAutoComplete } from 'src/pages/setting/syncTamplateForm';
import { useGetAssigneeList } from 'src/pages/tickets/hooks/useGetAssigneeList';
import { CustomAutocomplete } from 'src/pages/tickets/components/custom-autocomplete';

import { Form } from 'src/components/hook-form';

import { useChannelContext } from '../../hooks';

const _status = [
  { label: 'Local - 011', value: '011' },
  { label: 'Unified Communications - 9200', value: '9200' },
  { label: 'Toll free - 800', value: '800' },
];

export default function VoiceStep2() {
  const { state: channelState, onChangeStep, setField } = useChannelContext();
  const { voiceCallDetails } = channelState;

  const {
    _recordList,
    searchValue,
    open,
    isFetching,
    listScrollHandler,
    searchValueHandler,
    openListHandler,
    closeListHandler,
  } = useGetAssigneeList({ isDidPage: false });

  const { t } = useTranslate('translation');

  const fldList = [
    { name: 'DidType', required: true },
    { name: 'assignedId', required: true },
  ];

  const DidManageSchema = validationSchema(fldList, t);

  type DidManageSchemaType = zod.infer<typeof DidManageSchema>;

  const defaultValues: DidManageSchemaType = {
    DidType: '',
    assignedId: null,
  };

  const methods = useForm<any>({
    resolver: zodResolver(DidManageSchema),
    defaultValues,
    values: { assignedId: voiceCallDetails.assignedId, DidType: voiceCallDetails.DidType[0] },
  });

  const { handleSubmit, watch } = methods;

  const [didType, assignedId] = watch(['DidType', 'assignedId']);

  const onSubmit = handleSubmit(async (data: any) => {});

  const handleChange = ({ value, fieldName }: { value: string; fieldName: string }) => {
    setField('voiceCallDetails', {
      ...voiceCallDetails,
      [fieldName]: value,
    });
  };

  return (
    <Form methods={methods} onSubmit={onSubmit} style={{ height: '100%' }}>
      <Box sx={{ height: '90%', display: 'flex', flexDirection: 'column' }}>
        <Box>
          <Grid container spacing={3} justifyContent="space-between">
            <ChannelAutoComplete
              name="DidType"
              options={_status}
              label="Did Type"
              onChangeField={handleChange}
            />
      
            <CustomAutocomplete
              name="assignedId"
              label="Assigned To"
              isOptionEqualToValue={(a, b) => a.id === b.id}
              placeholder="Select a Team"
              options={_recordList}
              searchValue={searchValue}
              listScrollHandler={listScrollHandler}
              searchValueHandler={searchValueHandler}
              open={open}
              openListHandler={openListHandler}
              closeListHandler={closeListHandler}
              isFetching={isFetching}
              onChangeField={handleChange}
            />
          </Grid>
        </Box>

        <Box sx={{ flexGrow: 1 }} />
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Box>
            <Button variant="outlined" color="inherit" onClick={() => onChangeStep('back')}>
              {t('PREVIOUS')}
            </Button>
          </Box>
          <Box>
            <Button
              disabled={!assignedId || !didType}
              size="small"
              variant="contained"
              type="submit"
              onClick={() => onChangeStep('next')}
            >
              {t('NEXT')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Form>
  );
}
